/**
 * Command Dashboard — A4
 * Supervisor situational awareness. Matches prototype command.jsx exactly.
 *
 * Layout: Event banner → KPI strip (4) → Main grid (zone grid | alerts+movement)
 *         → Bottom row (teams | flags)
 *
 * Data:
 *   Alerts  - selected-operation stream owned by AppContext.
 *   Dashboard - selected-operation Command projection from Crowd API facade.
 *
 * No PII — zone names, anonymous density counts, display names only.
 */

import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Alert, Operation, Zone } from '@vigilo/shared';
import { useApp } from '@/shell/AppContext';
import {
  type ColorPalette,
  Icons, KLabel, StatusDot, Panel, Btn, SeverityColor, StatusColor,
  FONT_SIZE, FONT_WEIGHT, LETTER_SPACING, LINE_HEIGHT, SPACE, RADIUS, SIZE,
} from '@vigilo/design-system';
import { getCrowdC } from '../../design/crowdPalette';
import { useMockMode } from '@/shell/MockModeContext';
import { useVenues } from '../hooks/useVenues';
import {
  useCommandDashboard,
  type CommandDashboardProjection,
  type CommandDashboardTeamSummary,
  type CommandDashboardCrowdLoadTrendBucket,
  type CommandDashboardZoneStatusCard,
  type MetricsFreshnessStatus,
  type MovementTrendBucket,
} from '../hooks/useCommandDashboard';
import { useUsers } from '../hooks/useUsers';
import { useTeams, type CrowdTeam } from '../hooks/useTeams';
import {
  MOCK_ZONES, MOCK_DASHBOARD,
} from '../data/mock';
import {
  CROWD_CLIENT_ACTIVITY_ACTION,
  CROWD_CLIENT_RESOURCE_TYPE,
  useCrowdActivityAudit,
} from '../hooks/useCrowdActivityAudit';
import { useZoneAlertSummaries } from '../hooks/useZoneAlertSummaries';

import { alertCountColor, loadColor } from '../utils/zone-status';
// ── Helpers ────────────────────────────────────────────────────────────────────

function zoneCode(name: string): string {
  return name.split(/\s+/).filter(Boolean).map(w => w[0]?.toUpperCase() ?? '').join('').slice(0, 5);
}

function userInitials(displayName: string): string {
  return displayName.split(/[\s.]+/).filter(Boolean).map(w => w[0]?.toUpperCase() ?? '').join('').slice(0, 2);
}

function effectiveOperationTeamId(operation: Operation, zoneId: string): string | null {
  const assignment = operation.zoneTeamAssignments.find(item => item.zoneId === zoneId);
  if (!assignment) return operation.defaultTeamId ?? null;
  return assignment.inheritedFromDefault ? operation.defaultTeamId ?? null : assignment.teamId ?? null;
}

interface CommandAlertRow {
  alertId: string;
  operationId: string;
  zoneId: string;
  zoneName: string;
  alertType: string;
  severity: Alert['severity'];
  status: Alert['status'];
  triggeredAt: string;
  triggerCondition: string | null;
}

function commandAlertZoneDisplayName(alert: Alert, operationZoneNameById: ReadonlyMap<string, string>): string {
  const operationZoneName = operationZoneNameById.get(alert.zoneId)?.trim();
  if (operationZoneName) return operationZoneName;

  const projectedZoneName = alert.zoneName.trim();
  return projectedZoneName || alert.zoneId;
}

function projectCommandAlertRows(
  operation: Operation | null,
  alerts: Alert[],
  operationZoneNameById: ReadonlyMap<string, string>,
): CommandAlertRow[] {
  if (!operation) return [];
  const assignedZoneIds = new Set(operation.assignedZoneIds);
  return alerts
    .filter(alert => (
      alert.operationId === operation.id
      && assignedZoneIds.has(alert.zoneId)
      && alert.status !== 'resolved'
    ))
    .map(alert => ({
      alertId: alert.id,
      operationId: operation.id,
      zoneId: alert.zoneId,
      zoneName: commandAlertZoneDisplayName(alert, operationZoneNameById),
      alertType: alert.alertType,
      severity: alert.severity,
      status: alert.status,
      triggeredAt: alert.triggeredAt,
      triggerCondition: alert.triggerContext?.condition ?? null,
    }))
    .sort((a, b) => new Date(b.triggeredAt).getTime() - new Date(a.triggeredAt).getTime());
}

function buildMockTeamSummary(
  operation: Operation | null,
  zones: Zone[],
  teams: CrowdTeam[],
  commandAlertRows: Array<{ zoneId: string }>,
): CommandDashboardTeamSummary[] {
  if (!operation) return [];
  const activeTeamsById = new Map(teams.filter(team => team.status === 'active').map(team => [team.id, team]));
  const zoneIdsByTeam = new Map<string, string[]>();
  for (const zone of zones) {
    const teamId = effectiveOperationTeamId(operation, zone.id);
    if (!teamId || !activeTeamsById.has(teamId)) continue;
    const list = zoneIdsByTeam.get(teamId) ?? [];
    list.push(zone.id);
    zoneIdsByTeam.set(teamId, list);
  }
  return [...zoneIdsByTeam.entries()].map(([teamId, teamZoneIds]) => {
    const team = activeTeamsById.get(teamId)!;
    return {
      teamId,
      name: team.name,
      color: team.color,
      zoneIds: teamZoneIds,
      zoneCount: teamZoneIds.length,
      analystCount: team.memberUserIds.length,
      memberUserIds: team.memberUserIds,
      alertCount: commandAlertRows.filter(alert => teamZoneIds.includes(alert.zoneId)).length,
      isDefault: operation.defaultTeamId === teamId,
    };
  }).sort((a, b) => a.name.localeCompare(b.name));
}

// ── useElapsed ─────────────────────────────────────────────────────────────────

function useElapsed(startTs: string | null): string {
  const [elapsed, setElapsed] = useState('00:00:00');
  useEffect(() => {
    if (!startTs) {
      setElapsed('00:00:00');
      return;
    }

    const tick = () => {
      const diff = Math.max(0, Math.floor((Date.now() - new Date(startTs).getTime()) / 1000));
      const h = Math.floor(diff / 3600).toString().padStart(2, '0');
      const m = Math.floor((diff % 3600) / 60).toString().padStart(2, '0');
      const s = (diff % 60).toString().padStart(2, '0');
      setElapsed(`${h}:${m}:${s}`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [startTs]);
  return elapsed;
}

interface CommandOperationBannerState {
  eyebrow:     string;
  windowLabel: string;
  timerLabel:  string;
  timerValue:  string;
  canClose:    boolean;
  isReadOnly:  boolean;
}

function formatOperationDurationMs(durationMs: number): string {
  const totalSeconds = Math.max(0, Math.floor(durationMs / 1000));
  const h = Math.floor(totalSeconds / 3600).toString().padStart(2, '0');
  const m = Math.floor((totalSeconds % 3600) / 60).toString().padStart(2, '0');
  const s = (totalSeconds % 60).toString().padStart(2, '0');
  return `${h}:${m}:${s}`;
}

function fixedOperationDuration(operation: Operation): string {
  const startMs = Date.parse(operation.actualStart ?? operation.scheduledStart);
  const endMs = Date.parse(operation.actualEnd ?? operation.scheduledEnd);
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) return 'N/A';
  return formatOperationDurationMs(endMs - startMs);
}

function scheduledOperationCountdown(operation: Operation): string {
  const startMs = Date.parse(operation.scheduledStart);
  if (!Number.isFinite(startMs)) return 'N/A';
  return formatOperationDurationMs(startMs - Date.now());
}

function commandOperationBannerState(operation: Operation, liveElapsed: string): CommandOperationBannerState {
  if (operation.status === 'active') {
    return {
      eyebrow:     'Active Operation',
      windowLabel: 'Live operation window',
      timerLabel:  'Op time elapsed',
      timerValue:  liveElapsed,
      canClose:    true,
      isReadOnly:  false,
    };
  }

  if (operation.status === 'paused') {
    return {
      eyebrow:     'Paused Operation',
      windowLabel: 'Operation paused',
      timerLabel:  'Op time elapsed',
      timerValue:  liveElapsed,
      canClose:    true,
      isReadOnly:  false,
    };
  }

  if (operation.status === 'scheduled') {
    return {
      eyebrow:     'Scheduled Operation',
      windowLabel: 'Scheduled operation window',
      timerLabel:  'Starts in',
      timerValue:  scheduledOperationCountdown(operation),
      canClose:    false,
      isReadOnly:  true,
    };
  }

  return {
    eyebrow:     'Closed Operation',
    windowLabel: 'Operation closed',
    timerLabel:  'Operation duration',
    timerValue:  fixedOperationDuration(operation),
    canClose:    false,
    isReadOnly:  true,
  };
}

// ── KPICard ────────────────────────────────────────────────────────────────────

interface Breakdown { label: string; value: number; color: string; }

interface KPICardProps {
  C:          ColorPalette;
  label:      string;
  value:      number | string;
  unit?:      string;
  accent:     string | null;
  breakdown?: Breakdown[];
  sparkline?: CommandDashboardCrowdLoadTrendBucket[];
  footer?:    ReactNode;
  trend?:     'up' | 'down' | 'stable';
}

function formatPercent(value: number): string {
  const rounded = Math.round(value * 10) / 10;
  return Number.isInteger(rounded) ? rounded.toFixed(0) : rounded.toFixed(1);
}

type MovementProjectionStatus = MetricsFreshnessStatus | 'simulation' | 'loading' | 'error';

function movementProjectionStatus(
  trend: { freshnessStatus?: MetricsFreshnessStatus } | null | undefined,
  loading: boolean,
  error: string | null,
  isMock: boolean,
): MovementProjectionStatus {
  if (isMock) return 'simulation';
  if (error) return 'error';
  if (loading && !trend) return 'loading';
  return trend?.freshnessStatus ?? 'missing';
}

function sparklinePoints(
  buckets: CommandDashboardCrowdLoadTrendBucket[],
  width: number,
  height: number,
): string {
  const values = buckets
    .map(bucket => ({ value: bucket.crowdLoadIndex }))
    .filter((point): point is { value: number } => (
      typeof point.value === 'number' && Number.isFinite(point.value)
    ));
  if (values.length < 2) return '';
  const divisor = Math.max(1, values.length - 1);
  return values
    .map((point, index) => {
      const x = (index / divisor) * width;
      const y = height - (Math.max(0, Math.min(100, point.value)) / 100) * (height - 4) - 2;
      return `${x},${y}`;
    })
    .join(' ');
}

function KPICard({ C, label, value, unit, accent, breakdown, sparkline, footer, trend }: KPICardProps) {
  const accentColor = accent ?? C.text;
  const loadTrendPoints = sparkline ? sparklinePoints(sparkline, 200, 30) : '';
  return (
    <Panel C={C} style={{ padding: `${SPACE[14]}px ${SPACE[16]}px` }}>
      <KLabel C={C}>{label}</KLabel>

      {/* Value row */}
      <div style={{
        display: 'flex', alignItems: 'baseline', gap: SPACE[4],
        marginBottom: breakdown ? SPACE[10] : SPACE[4],
      }}>
        <span style={{
          fontSize: FONT_SIZE.hero, fontWeight: FONT_WEIGHT.black,
          color: accentColor, lineHeight: LINE_HEIGHT.tight,
          fontVariantNumeric: 'tabular-nums',
        }}>{value}</span>
        {unit && (
          <span style={{ fontSize: FONT_SIZE.heading, color: C.muted, fontWeight: FONT_WEIGHT.medium }}>
            {unit}
          </span>
        )}
        {trend && (
          <span style={{
            marginLeft: 'auto',
            color: trend === 'up' ? C.ember : trend === 'down' ? C.green : C.muted,
          }}>
            {trend === 'up'   ? <Icons.ArrowU size={SIZE.iconMd} /> :
             trend === 'down' ? <Icons.ArrowD size={SIZE.iconMd} /> :
                                <Icons.ArrowR size={SIZE.iconMd} />}
          </span>
        )}
      </div>

      {/* Breakdown dots */}
      {breakdown && (
        <div style={{ display: 'flex', gap: SPACE[12], paddingTop: SPACE[8], borderTop: `1px solid ${C.border}` }}>
          {breakdown.map(b => (
            <div key={b.label} style={{ display: 'flex', alignItems: 'center', gap: SPACE[4] }}>
              <StatusDot color={b.color} size={6} />
              <span style={{ fontSize: FONT_SIZE.xs, color: C.muted }}>{b.label}</span>
              <span style={{ fontSize: FONT_SIZE.base, fontWeight: FONT_WEIGHT.bold, color: C.text, fontFamily: 'monospace' }}>
                {b.value}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Sparkline */}
      {sparkline && (
        <svg viewBox="0 0 200 30" style={{ width: '100%', height: SIZE.kpiSparkline }}>
          {loadTrendPoints && (
            <polyline
              points={loadTrendPoints}
              fill="none" stroke={accentColor} strokeWidth="1.5"
            />
          )}
        </svg>
      )}

      {footer && (
        <div style={{ fontSize: FONT_SIZE.sm, color: C.muted, marginTop: SPACE[4] }}>{footer}</div>
      )}
    </Panel>
  );
}

// ── ZoneCard ───────────────────────────────────────────────────────────────────

function projectedZoneStatusColor(C: ColorPalette, status: Zone['status'] | null): string {
  return status ? StatusColor(C, status) : C.muted;
}

function ZoneCard({ C, card, activeAlerts, onClick }: {
  C:            ColorPalette;
  card:         CommandDashboardZoneStatusCard;
  activeAlerts: number;
  onClick:      () => void;
}) {
  const stColor = projectedZoneStatusColor(C, card.status);
  const code    = zoneCode(card.zoneName);
  const occupancyLabel = card.occupancyCount === null || card.occupancyCount === undefined
    ? '-'
    : card.occupancyCount.toLocaleString();

  return (
    <div onClick={onClick} style={{
      background: C.surface, border: `1px solid ${C.border}`,
      borderTop: `${SIZE.severityBar}px solid ${stColor}`,
      borderRadius: RADIUS.card, padding: SPACE[12],
      cursor: 'pointer', transition: 'all 120ms',
    }}>
      {/* Code + alert badge */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: SPACE[6] }}>
        <span style={{
          fontSize: FONT_SIZE.label, fontWeight: FONT_WEIGHT.bold,
          color: stColor, letterSpacing: LETTER_SPACING.label, textTransform: 'uppercase',
        }}>{code}</span>
        {activeAlerts > 0 && (
          <span style={{
            fontSize: FONT_SIZE.label, fontWeight: FONT_WEIGHT.bold,
            color: '#fff', background: alertCountColor(C),
            padding: `${SPACE[1]}px ${SPACE[6]}px`, borderRadius: RADIUS.tab,
          }}>{activeAlerts}</span>
        )}
      </div>

      {/* Name */}
      <div style={{ fontSize: FONT_SIZE.body, fontWeight: FONT_WEIGHT.bold, color: C.text, marginBottom: SPACE[2] }}>
        {card.zoneName}
      </div>

      {/* Type */}
      <div style={{ fontSize: FONT_SIZE.xs, color: C.muted, marginBottom: SPACE[8] }}>
        {card.zoneType.replace(/_/g, ' ')}
      </div>

      {/* Density bar + % */}
      <div style={{ display: 'flex', alignItems: 'center', gap: SPACE[6], marginBottom: SPACE[4] }}>
        <div style={{ flex: 1, height: SIZE.barMd, background: C.faint, borderRadius: RADIUS.sm, overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${Math.min(card.occupancyPct ?? 0, 100)}%`, background: stColor, transition: 'width 800ms ease' }} />
        </div>
        <span style={{
          fontSize: FONT_SIZE.base, fontWeight: FONT_WEIGHT.bold,
          color: C.text, fontFamily: 'monospace',
          minWidth: 32, textAlign: 'right',
        }}>{card.occupancyPct === null || card.occupancyPct === undefined ? 'N/A' : `${formatPercent(card.occupancyPct)}%`}</span>
      </div>

      {/* Occupancy / capacity */}
      <div style={{ fontSize: FONT_SIZE.label, color: C.muted, fontFamily: 'monospace' }}>
        {occupancyLabel} / {card.capacity.toLocaleString()}
      </div>
    </div>
  );
}

// ── FlowChart ──────────────────────────────────────────────────────────────────

function FlowChart({ C, buckets, projectionStatus }: {
  C: ColorPalette;
  buckets: MovementTrendBucket[];
  projectionStatus: MovementProjectionStatus;
}) {
  const inbound  = buckets.map(bucket => bucket.inflow);
  const outbound = buckets.map(bucket => bucket.outflow);
  const max = Math.max(1, ...inbound, ...outbound);
  const w = 360, h = 100;
  const canRenderTrend = projectionStatus === 'fresh' || projectionStatus === 'simulation';

  const pts = (data: number[]) =>
    data.map((v, i) => {
      const divisor = Math.max(1, data.length - 1);
      return `${(i / divisor) * w},${h - (v / max) * (h - 6) - 3}`;
    }).join(' ');

  if (!canRenderTrend || buckets.length === 0) {
    return (
      <svg viewBox={`0 0 ${w} ${h}`} style={{ width: '100%', height: h }}>
        {[25, 50, 75].map(y => (
          <line key={y}
            x1="0" y1={h - (y / 100) * (h - 6) - 3}
            x2={w} y2={h - (y / 100) * (h - 6) - 3}
            stroke={C.border2} strokeDasharray="2,3" strokeWidth="0.5"
          />
        ))}
      </svg>
    );
  }

  return (
    <svg viewBox={`0 0 ${w} ${h}`} style={{ width: '100%', height: h }}>
      {[25, 50, 75].map(y => (
        <line key={y}
          x1="0" y1={h - (y / 100) * (h - 6) - 3}
          x2={w} y2={h - (y / 100) * (h - 6) - 3}
          stroke={C.border2} strokeDasharray="2,3" strokeWidth="0.5"
        />
      ))}
      {/* Inbound: filled area + solid line */}
      <polyline points={`0,${h} ${pts(inbound)} ${w},${h}`}  fill={`${C.teal}33`} />
      <polyline points={pts(inbound)}  fill="none" stroke={C.teal}  strokeWidth="2" strokeLinejoin="round" />
      {/* Outbound: dashed line only */}
      <polyline points={pts(outbound)} fill="none" stroke={C.amber} strokeWidth="2" strokeLinejoin="round" strokeDasharray="4,2" />
    </svg>
  );
}

// ── CommandScreen ──────────────────────────────────────────────────────────────

export function CommandScreen() {
  const navigate  = useNavigate();
  const recordActivity = useCrowdActivityAudit();
  const {
    theme,
    selectedOperation,
    selectedOperationAlerts,
    selectedOperationAlertSummary,
    openCloseModal,
    openOperationModal,
    currentUser,
  } = useApp();
  const C         = getCrowdC(theme);

  const canClose  = currentUser.role === 'command_supervisor' || currentUser.role === 'system_admin';

  const { isMock } = useMockMode();

  // Alerts are centralized in AppContext for selected Operation.
  const { venues }    = useVenues();
  const userState     = useUsers();
  const teamState     = useTeams();
  const commandDashboard = useCommandDashboard(
    selectedOperation?.id ?? null,
    !isMock && Boolean(selectedOperation),
  );

  const dashboard: CommandDashboardProjection | null = isMock
    ? MOCK_DASHBOARD as CommandDashboardProjection
    : commandDashboard.dashboard;
  const users     = userState.users;
  const usersById = new Map(users.map(user => [user.id, user]));
  const flagRows  = dashboard?.flagRows ?? [];
  const activeFlagCount = dashboard?.flagSummary.active ?? null;
  const flagLimit = dashboard?.flagSummary.limit ?? null;
  const operation = selectedOperation;
  const assignedZoneIds = operation ? new Set(operation.assignedZoneIds) : null;
  const zoneCards = (dashboard?.zoneStatusCards ?? []).filter(card => (
    !assignedZoneIds || assignedZoneIds.has(card.zoneId)
  ));
  const zoneAlertSummaries = useZoneAlertSummaries(
    selectedOperation?.id ?? null,
    zoneCards.map(card => card.zoneId),
    Boolean(!isMock && selectedOperation),
  );
  const displayZoneCards = useMemo(() => (
    isMock
      ? zoneCards
      : zoneCards.map(card => ({
        ...card,
        alertCount: zoneAlertSummaries.summariesByZoneId.get(card.zoneId)?.active ?? card.alertCount,
      }))
  ), [isMock, zoneAlertSummaries.summariesByZoneId, zoneCards]);
  const mockZones = isMock
    ? (operation ? MOCK_ZONES.filter(zone => operation.assignedZoneIds.includes(zone.id)) : MOCK_ZONES)
    : [];
  const operationZoneNameById = useMemo(() => {
    const zoneNames = new Map<string, string>();
    const scopedZoneIds = new Set(operation?.assignedZoneIds ?? []);
    for (const card of displayZoneCards) {
      if (scopedZoneIds.has(card.zoneId) && card.zoneName.trim()) {
        zoneNames.set(card.zoneId, card.zoneName.trim());
      }
    }
    return zoneNames;
  }, [operation?.assignedZoneIds, displayZoneCards]);

  // Op elapsed from actual start
  const opStartTs = operation?.actualStart ?? operation?.scheduledStart ?? null;
  const opElapsed = useElapsed(opStartTs);

  function venueName(venueId: string): string {
    return venues.find(v => v.id === venueId)?.name ?? 'Unknown venue';
  }

  function openMap(zoneId?: string): void {
    if (!zoneId) {
      recordActivity({
        action: CROWD_CLIENT_ACTIVITY_ACTION.drilldownOpened,
        page: '/command',
        operationId: selectedOperation?.id,
        resourceType: CROWD_CLIENT_RESOURCE_TYPE.drilldown,
        resourceId: 'map',
        metadata: { sourcePage: '/command', targetPage: '/map' },
      });
      navigate('/map');
      return;
    }
    recordActivity({
      action: CROWD_CLIENT_ACTIVITY_ACTION.drilldownOpened,
      page: '/command',
      operationId: selectedOperation?.id,
      resourceType: CROWD_CLIENT_RESOURCE_TYPE.zone,
      resourceId: zoneId,
      metadata: { sourcePage: '/command', targetPage: '/map' },
    });
    navigate(`/map?zoneId=${encodeURIComponent(zoneId)}`, { state: { focusZoneId: zoneId } });
  }

  // ── Derived KPIs ──────────────────────────────────────────────────────────────
  const commandAlertRows = projectCommandAlertRows(selectedOperation, selectedOperationAlerts, operationZoneNameById);
  const commandActiveAlertCount = isMock ? commandAlertRows.length : selectedOperationAlertSummary.active;
  const highestProjection = dashboard?.kpis.highestOccupancyZone ?? null;
  const highestZoneStatus = highestProjection
    ? displayZoneCards.find(card => card.zoneId === highestProjection.zoneId)?.status ?? null
    : null;
  const projectedZoneBreakdown = dashboard
    ? [
      { label: 'Normal',  value: dashboard.kpis.zonesNormal,    color: C.green },
      { label: 'Warning', value: dashboard.kpis.zonesInWarning, color: C.amber },
      { label: 'Alert',   value: dashboard.kpis.zonesInAlert,   color: C.ember },
    ]
    : undefined;
  const crowdLoad = dashboard?.kpis.crowdLoadIndex ?? null;
  const highestUtil = highestProjection?.utilisation ?? null;

  const totalInflow = dashboard?.movementTrend.totalInflow ?? 0;
  const totalOutflow = dashboard?.movementTrend.totalOutflow ?? 0;
  const netFlow = dashboard?.movementTrend.netFlow ?? 0;
  const movementTrendProjection = dashboard?.movementTrend ?? null;
  const movementStatus = movementProjectionStatus(
    movementTrendProjection,
    commandDashboard.loading,
    commandDashboard.error,
    isMock,
  );
  const canShowMovementTotals = movementStatus === 'fresh' || movementStatus === 'simulation';
  const movementBuckets = dashboard?.movementTrend.buckets ?? [];
  const netStr = !canShowMovementTotals
    ? 'N/A'
    : netFlow >= 0
    ? `+${netFlow.toLocaleString()}`
    : `−${Math.abs(netFlow).toLocaleString()}`;
  const operationBanner = operation ? commandOperationBannerState(operation, opElapsed) : null;
  const teamRows = useMemo(() => (
    isMock
      ? buildMockTeamSummary(operation, mockZones, teamState.teams, commandAlertRows)
      : (commandDashboard.dashboard?.teamSummary ?? []).map(team => ({
        ...team,
        alertCount: team.zoneIds.reduce(
          (total, zoneId) => total + (zoneAlertSummaries.summariesByZoneId.get(zoneId)?.active ?? 0),
          0,
        ),
      }))
  ), [commandAlertRows, commandDashboard.dashboard?.teamSummary, isMock, operation, teamState.teams, mockZones, zoneAlertSummaries.summariesByZoneId]);

  // Empty state — no active operation
  if (!operation) {
    return (
      <div style={{
        flex: 1, display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        fontFamily: "'Albert Sans', sans-serif", gap: SPACE[14],
        padding: SPACE[20],
      }}>
        <div style={{
          width: SIZE.avatarLg, height: SIZE.avatarLg, borderRadius: RADIUS.card,
          background: C.faint, border: `1px solid ${C.border2}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: C.muted,
        }}>
          <Icons.Shield size={SIZE.iconLg} />
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            fontSize: FONT_SIZE.subheading, fontWeight: FONT_WEIGHT.bold,
            color: C.text, marginBottom: SPACE[6],
          }}>No active operation</div>
          <div style={{ fontSize: FONT_SIZE.body, color: C.muted, marginBottom: SPACE[16] }}>
            Start a new operation to begin monitoring.
          </div>
          {canClose && (
            <Btn C={C} variant="primary" icon={<Icons.Plus size={SIZE.iconXs} />} onClick={openOperationModal}>
              Start Operation
            </Btn>
          )}
        </div>
      </div>
    );
  }

  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: SPACE[20], fontFamily: "'Albert Sans', sans-serif" }}>

      {/* ── Event banner ──────────────────────────────────────────────────────── */}
      <div style={{
        background: C.panel, border: `1px solid ${C.border2}`,
        borderLeft: `4px solid ${C.amber}`,
        borderRadius: RADIUS.lg, padding: `${SPACE[14]}px ${SPACE[18]}px`,
        marginBottom: SPACE[16], display: 'flex', alignItems: 'center', gap: SPACE[18],
      }}>
        {/* Shield icon */}
        <div style={{
          width: SIZE.avatarLg, height: SIZE.avatarLg, borderRadius: RADIUS.lg,
          background: C.amberDim, border: `1px solid ${C.amber}55`,
          color: C.amber, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <Icons.Shield size={SIZE.iconLg} />
        </div>

        {/* Operation details */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: FONT_SIZE.xs, fontWeight: FONT_WEIGHT.bold,
            letterSpacing: LETTER_SPACING.klabel, textTransform: 'uppercase',
            color: C.amber, marginBottom: SPACE[2],
          }}>{operationBanner?.eyebrow ?? 'Selected Operation'}</div>
          <div style={{ fontSize: FONT_SIZE.heading, fontWeight: FONT_WEIGHT.bold, color: C.text }}>
            {operation?.name ?? 'No active operation'}
          </div>
          <div style={{ fontSize: FONT_SIZE.basePlus, color: C.muted }}>
            {operation ? venueName(operation.venueId) : '—'} ·{' '}
            {operation ? new Date(operation.scheduledStart).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'} · {operationBanner?.windowLabel ?? 'Operation context required'}
          </div>
        </div>

        {/* Elapsed timer + close button */}
        <div style={{ display: 'flex', alignItems: 'center', gap: SPACE[16], flexShrink: 0 }}>
          <div style={{ textAlign: 'right' }}>
            <div style={{
              fontSize: FONT_SIZE.label, color: C.muted,
              letterSpacing: LETTER_SPACING.field, textTransform: 'uppercase', marginBottom: SPACE[3],
            }}>{operationBanner?.timerLabel ?? 'Operation time'}</div>
            <div style={{
              fontSize: FONT_SIZE.title, fontWeight: FONT_WEIGHT.bold, color: C.text,
              fontFamily: 'monospace', fontVariantNumeric: 'tabular-nums',
            }}>{operationBanner?.timerValue ?? '00:00:00'}</div>
          </div>
          {canClose && operationBanner?.canClose && !operationBanner.isReadOnly && (
            <Btn
              C={C}
              variant="danger"
              size="sm"
              icon={<Icons.X size={SIZE.iconXs} />}
              onClick={openCloseModal}
            >
              Close Operation
            </Btn>
          )}
        </div>
      </div>

      {/* ── KPI strip ─────────────────────────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: SPACE[12], marginBottom: SPACE[16] }}>

        <KPICard C={C}
          label="Monitored Zones"
          value={dashboard?.kpis.totalZones ?? 'N/A'}
          accent={null}
          breakdown={projectedZoneBreakdown}
        />

        <KPICard C={C}
          label="Active Alerts"
          value={commandActiveAlertCount}
          accent={commandActiveAlertCount > 0 ? alertCountColor(C) : C.green}
          breakdown={[
            { label: 'Open',      value: isMock ? commandAlertRows.filter(a => a.status === 'open').length : selectedOperationAlertSummary.open, color: C.ember },
            { label: 'Acknowledged', value: isMock ? commandAlertRows.filter(a => a.status === 'acknowledged').length : selectedOperationAlertSummary.acknowledged, color: C.amber },
            { label: 'Escalated', value: isMock ? commandAlertRows.filter(a => a.status === 'escalated').length : selectedOperationAlertSummary.escalated, color: C.critical },
          ]}
        />

        <KPICard C={C}
          label="Crowd Load Index"
          value={crowdLoad === null ? 'N/A' : formatPercent(crowdLoad)}
          unit={crowdLoad === null ? undefined : '%'}
          accent={crowdLoad === null ? C.muted : loadColor(crowdLoad, C)}
          sparkline={dashboard?.crowdLoadTrend?.buckets}
        />

        <KPICard C={C}
          label="Highest Occupancy"
          value={highestUtil === null ? 'N/A' : formatPercent(highestUtil)}
          unit={highestUtil === null ? undefined : '%'}
          accent={projectedZoneStatusColor(C, highestZoneStatus)}
          trend={highestUtil === null ? undefined : highestUtil >= 85 ? 'up' : highestUtil >= 70 ? 'up' : undefined}
          footer={highestProjection
            ? <>{zoneCode(highestProjection.zoneName)} · <span style={{ color: C.text, fontWeight: FONT_WEIGHT.semibold }}>{highestProjection.zoneName}</span></>
            : null}
        />
      </div>

      {/* ── Main grid ─────────────────────────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: SPACE[14] }}>

        {/* Left: Zone status grid */}
        <Panel C={C} style={{ padding: 0 }}>
          <div style={{
            padding: `${SPACE[14]}px ${SPACE[18]}px`, borderBottom: `1px solid ${C.border2}`,
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          }}>
            <div>
              <KLabel C={C} mb={2}>Zone Status Grid</KLabel>
              <div style={{ fontSize: FONT_SIZE.basePlus, color: C.text }}>
                All {dashboard?.kpis.totalZones ?? 'N/A'} monitored zones · click for detail
              </div>
            </div>
            <Btn C={C} variant="ghost" size="sm" icon={<Icons.Map size={SIZE.iconXs} />} onClick={() => openMap()}>
              Open map
            </Btn>
          </div>
          <div style={{ padding: SPACE[14], display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: SPACE[10] }}>
            {displayZoneCards.map(card => {
              return (
                <ZoneCard
                  key={card.zoneId} C={C} card={card}
                  activeAlerts={card.alertCount}
                  onClick={() => openMap(card.zoneId)}
                />
              );
            })}
            {displayZoneCards.length === 0 && (
              <div style={{
                gridColumn: '1 / -1',
                padding: `${SPACE[24]}px ${SPACE[18]}px`,
                textAlign: 'center', fontSize: FONT_SIZE.base, color: C.muted,
              }}>No projected zone status available</div>
            )}
          </div>
        </Panel>

        {/* Right column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: SPACE[14] }}>

          {/* Active alerts panel */}
          <Panel C={C} style={{ padding: 0 }}>
            <div style={{
              padding: `${SPACE[14]}px ${SPACE[18]}px`, borderBottom: `1px solid ${C.border2}`,
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            }}>
              <KLabel C={C} mb={0}>Active Alerts</KLabel>
              <span style={{ fontSize: FONT_SIZE.xs, color: C.muted, fontFamily: 'monospace' }}>
                {commandAlertRows.length === commandActiveAlertCount
                  ? `${commandActiveAlertCount} active`
                  : `${commandAlertRows.length} shown of ${commandActiveAlertCount}`}
              </span>
            </div>
            <div style={{ maxHeight: SIZE.scrollListMax, overflowY: 'auto' }}>
              {commandAlertRows.length === 0 ? (
                <div style={{
                  padding: `${SPACE[24]}px ${SPACE[18]}px`,
                  textAlign: 'center', fontSize: FONT_SIZE.base, color: C.muted,
                }}>No active alerts</div>
              ) : commandAlertRows.map(a => {
                const sevColor = SeverityColor(C, a.severity);
                const stColor  = a.status === 'open'         ? C.ember
                               : a.status === 'acknowledged' ? C.amber
                               : C.critical;
                const diff     = Math.floor((Date.now() - new Date(a.triggeredAt).getTime()) / 1000);
                const elapsed  = diff < 60 ? `${diff}s` : diff < 3600 ? `${Math.floor(diff / 60)}m` : `${Math.floor(diff / 3600)}h`;
                return (
                  <div key={a.alertId} onClick={() => {
                    recordActivity({
                      action: CROWD_CLIENT_ACTIVITY_ACTION.drilldownOpened,
                      page: '/command',
                      operationId: selectedOperation?.id,
                      resourceType: CROWD_CLIENT_RESOURCE_TYPE.alert,
                      resourceId: a.alertId,
                      metadata: { sourcePage: '/command', targetPage: '/alerts' },
                    });
                    navigate('/alerts');
                  }} style={{
                    padding: `${SPACE[11]}px ${SPACE[18]}px`, borderBottom: `1px solid ${C.border}`,
                    borderLeft: `${SIZE.severityBar}px solid ${sevColor}`, cursor: 'pointer',
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: SPACE[3] }}>
                      <span style={{
                        fontSize: FONT_SIZE.sm, fontWeight: FONT_WEIGHT.bold,
                        color: sevColor, letterSpacing: LETTER_SPACING.wide, textTransform: 'uppercase',
                      }}>{a.alertType.replace(/_/g, ' ')}</span>
                      <span style={{ fontSize: FONT_SIZE.micro, fontFamily: 'monospace', color: C.muted }}>
                        {elapsed}
                      </span>
                    </div>
                    <div style={{
                      fontSize: FONT_SIZE.basePlus, fontWeight: FONT_WEIGHT.semibold,
                      color: C.text, marginBottom: SPACE[3],
                    }}>{a.zoneName}</div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: FONT_SIZE.xs, color: C.muted }}>
                        {a.triggerCondition ?? '-'}
                      </span>
                      <span style={{
                        fontSize: FONT_SIZE.label, fontWeight: FONT_WEIGHT.bold,
                        color: stColor, letterSpacing: LETTER_SPACING.label, textTransform: 'uppercase',
                        background: `${stColor}22`, padding: `${SPACE[1]}px ${SPACE[6]}px`, borderRadius: RADIUS.sm,
                      }}>{a.status}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </Panel>

          {/* Movement trend */}
          <Panel C={C} style={{ padding: SPACE[18] }}>
            <KLabel C={C}>Movement Trend — Last Hour</KLabel>
            <FlowChart C={C} buckets={movementBuckets} projectionStatus={movementStatus} />
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: SPACE[10], fontSize: FONT_SIZE.sm }}>
              <span style={{ color: C.muted }}>
                <StatusDot color={C.teal} size={5} />{' '}Inbound{' '}
                <span style={{ color: C.text, fontWeight: FONT_WEIGHT.bold, fontFamily: 'monospace' }}>
                  {canShowMovementTotals ? `+${totalInflow.toLocaleString()}` : 'N/A'}
                </span>
              </span>
              <span style={{ color: C.muted }}>
                <StatusDot color={C.amber} size={5} />{' '}Outbound{' '}
                <span style={{ color: C.text, fontWeight: FONT_WEIGHT.bold, fontFamily: 'monospace' }}>
                  {canShowMovementTotals ? `+${totalOutflow.toLocaleString()}` : 'N/A'}
                </span>
              </span>
              <span style={{ color: C.muted }}>
                Net{' '}
                <span style={{ color: C.ember, fontWeight: FONT_WEIGHT.bold, fontFamily: 'monospace' }}>
                  {netStr}
                </span>
              </span>
            </div>
          </Panel>
        </div>
      </div>

      {/* ── Bottom row ─────────────────────────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: SPACE[14], marginTop: SPACE[14] }}>

        {/* Operations teams */}
        <Panel C={C} style={{ padding: SPACE[18] }}>
          <KLabel C={C}>Operations Teams</KLabel>
          {teamRows.length === 0 ? (
            <div style={{
              padding: `${SPACE[24]}px ${SPACE[8]}px`,
              textAlign: 'center', fontSize: FONT_SIZE.base, color: C.muted,
            }}>No assigned teams for this operation</div>
          ) : teamRows.map(team => {
            const teamMembers = team.memberUserIds
              .map(userId => usersById.get(userId))
              .filter((user): user is NonNullable<typeof user> => Boolean(user));
            const teamColor = team.color ?? C.teal;
            return (
              <div key={team.teamId} style={{
                display: 'flex', alignItems: 'center', gap: SPACE[14],
                padding: `${SPACE[10]}px 0`, borderBottom: `1px solid ${C.border}`,
              }}>
                {/* Team abbreviation icon */}
                <div style={{
                  width: SIZE.teamIcon, height: SIZE.teamIcon, borderRadius: RADIUS.lg,
                  background: C.faint, color: teamColor,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  fontSize: FONT_SIZE.base, fontWeight: FONT_WEIGHT.bold, letterSpacing: LETTER_SPACING.wide,
                }}>
                  {team.name.split(' ')[0]?.slice(0, 3).toUpperCase()}
                </div>

                {/* Name + zone/member count */}
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: FONT_SIZE.item, fontWeight: FONT_WEIGHT.semibold, color: C.text }}>
                    {team.name}
                  </div>
                  <div style={{ fontSize: FONT_SIZE.sm, color: C.muted }}>
                    {team.zoneCount} zones · {team.analystCount} analysts
                    {team.isDefault ? ' · default' : ''}
                  </div>
                </div>

                {/* Member initials */}
                <div style={{ display: 'flex', gap: SPACE[4] }}>
                  {teamMembers.slice(0, 3).map(m => (
                    <span key={m.id} title={m.displayName} style={{
                      width: SIZE.avatarSm, height: SIZE.avatarSm, borderRadius: '50%',
                      background: C.amberDim, border: `1px solid ${C.amber}55`,
                      color: C.amber, fontSize: FONT_SIZE.label, fontWeight: FONT_WEIGHT.bold,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}>{userInitials(m.displayName)}</span>
                  ))}
                </div>

                {/* Team active alert count */}
                <div style={{ textAlign: 'right', minWidth: SIZE.teamAlertCount }}>
                  <div style={{
                    fontSize: FONT_SIZE.heading, fontWeight: FONT_WEIGHT.bold,
                    color: team.alertCount > 0 ? alertCountColor(C) : C.text, lineHeight: LINE_HEIGHT.tight,
                  }}>{team.alertCount}</div>
                  <div style={{
                    fontSize: FONT_SIZE.label, color: C.muted,
                    letterSpacing: LETTER_SPACING.label, textTransform: 'uppercase',
                  }}>Alerts</div>
                </div>
              </div>
            );
          })}
        </Panel>

        {/* Flagged tokens summary */}
        <Panel C={C} style={{ padding: SPACE[18] }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: SPACE[10] }}>
            <KLabel C={C} mb={0}>Flagged Tokens — Session</KLabel>
            <span style={{ fontSize: FONT_SIZE.xs, color: C.muted, fontFamily: 'monospace' }}>
              {activeFlagCount ?? 'N/A'} of {flagLimit ?? 'N/A'} max
            </span>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: SPACE[6] }}>
            {flagRows.slice(0, 4).map(f => {
              const currentZoneId  = f.currentZoneId;
              const projectedZoneName = f.currentZoneName;
              const currentZone    = displayZoneCards.find(card => card.zoneId === currentZoneId)?.zoneName ?? currentZoneId ?? '-';
              const isUrgent       = f.priority === 'urgent';

              return (
                <div key={f.flagId} onClick={() => {
                  recordActivity({
                    action: CROWD_CLIENT_ACTIVITY_ACTION.drilldownOpened,
                    page: '/command',
                    operationId: selectedOperation?.id,
                    resourceType: CROWD_CLIENT_RESOURCE_TYPE.anonymousFlag,
                    resourceId: f.flagId,
                    metadata: { sourcePage: '/command', targetPage: '/flags' },
                  });
                  navigate('/flags');
                }} style={{
                  display: 'flex', alignItems: 'center', gap: SPACE[10],
                  padding: `${SPACE[8]}px ${SPACE[10]}px`,
                  background: C.surface, border: `1px solid ${C.border}`,
                  borderLeft: `${SIZE.severityBar}px solid ${isUrgent ? C.critical : C.amber}`,
                  borderRadius: RADIUS.md, cursor: 'pointer',
                }}>
                  <span style={{
                    fontSize: FONT_SIZE.micro, fontFamily: 'monospace', color: C.muted,
                    background: C.faint, padding: `${SPACE[2]}px ${SPACE[5]}px`, borderRadius: RADIUS.xs,
                    flexShrink: 0,
                  }}>{f.tokenRef.slice(0, 8)}</span>
                  <span style={{
                    flex: 1, fontSize: FONT_SIZE.base, color: C.text,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>{f.behaviourNote}</span>
                  <span style={{ fontSize: FONT_SIZE.micro, color: C.muted, fontFamily: 'monospace', flexShrink: 0 }}>
                    {projectedZoneName ?? currentZone}
                  </span>
                </div>
              );
            })}
            {flagRows.length === 0 && (
              <div style={{
                padding: `${SPACE[10]}px ${SPACE[12]}px`,
                background: C.surface,
                border: `1px solid ${C.border}`,
                borderRadius: RADIUS.md,
                color: C.muted,
                fontSize: FONT_SIZE.base,
              }}>
                No active flagged tokens for this Operation
              </div>
            )}
          </div>

          <div style={{
            fontSize: FONT_SIZE.micro, color: C.muted,
            marginTop: SPACE[10], paddingTop: SPACE[10], borderTop: `1px solid ${C.border}`,
            lineHeight: LINE_HEIGHT.relaxed,
          }}>
            Anonymous behaviour tokens. No subscriber identity is collected or available within this application.
          </div>
        </Panel>
      </div>
    </div>
  );
}
